import { Button, SelectDropDown, TabItem } from '@wot/components';
import { ICategoryType, ISessionStatus } from '@wot/typings';
import {
  addDays,
  addMonths,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  parseISO,
  parseJSON,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subMonths
} from 'date-fns';
import { format } from 'date-fns/esm';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import WorldMap, { type DataItem, type ISOCode } from 'react-svg-worldmap';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { RISING } from '../../assets/icons';
import { CLASSES_ICON, PLANS_ICON, STUDENTS_ICON, TUTORS_ICON } from '../../assets/icons/dashboard';
import { TabBar } from '../../components/TabBar';
import { Table, TableBody, TableHeading, TableHeadingCol, TableRow, TableRowItemData } from '../../components/Table';
import xhr from '../../utils/fetch';
// @ts-ignore
import CountryQuery from 'country-query';
import plansApi from '../../api/plans';
import { Dropdown } from '../../components/DropDown';
import StatusChip from '../../components/StatusChip';
import { currencyFormatter } from '../../utils/functions';
import { LPlan } from '../../interfaces/Plan';

export default function Dashboard() {
  const [range, setRange] = useState('Today');
  const [currRangeDate, setCurrRangeDate] = useState({
    start: '',
    end: ''
  });
  const [stats, setStats] = useState({
    plans: { count: 0, percentage: 0, growing: true },
    classes: { count: 0, percentage: 0, growing: true },
    students: { count: 0, percentage: 0, growing: true },
    tutors: { count: 0, percentage: 0, growing: true }
  });

  function getCountAndPercentage(res: any, key: string) {
    let count = res.data[key].count ? res.data[key].count : 0;
    let prevCount = res.data[key].previousCount ? res.data[key].previousCount : 0;
    let percentage = prevCount !== 0 ? (count / prevCount) * 100 : 0;
    return { count, percentage: Number(percentage.toFixed(2)), growing: count >= prevCount };
  }

  useEffect(() => {
    if (!currRangeDate.start) {
      return;
    }
    xhr({
      url: `/a/stats?startAfter=${currRangeDate.start}&endBefore=${currRangeDate.end}`,
      method: 'GET'
    })
      .then((res) => {
        if (res.status === 'OK') {
          setStats({
            ...stats,
            plans: getCountAndPercentage(res, 'plansCount'),
            classes: getCountAndPercentage(res, 'classesCount'),
            students: getCountAndPercentage(res, 'studentsCount'),
            tutors: getCountAndPercentage(res, 'tutorsCount')
          });
        }
      })
      .catch((err) => err);
  }, [currRangeDate]);

  useEffect(() => {
    if (range === 'Today') {
      setCurrRangeDate({
        start: startOfDay(new Date()).toString(),
        end: endOfDay(new Date()).toString()
      });
    }
    if (range === 'This Week') {
      setCurrRangeDate({
        start: startOfWeek(new Date()).toString(),
        end: endOfWeek(new Date()).toString()
      });
    }
    if (range === 'This Month') {
      setCurrRangeDate({
        start: startOfMonth(new Date()).toString(),
        end: endOfMonth(new Date()).toString()
      });
    }
    if (range === 'Last Month') {
      setCurrRangeDate({
        start: startOfMonth(subMonths(new Date(), 1)).toString(),
        end: endOfMonth(subMonths(new Date(), 1)).toString()
      });
    }
    if (range === 'All Time') {
      setCurrRangeDate({
        start: new Date(0).toString(),
        end: ''
      });
    }
  }, [range]);

  return (
    <div className="w-full flex flex-col gap-4 p-2 grow bg-neutralBlue2">
      <div className="w-full flex justify-between">
        <h1 className="text-xl font-semibold">Dashboard</h1>
        <div className="w-full max-w-[164px]">
          <SelectDropDown
            placeholder="Select Range"
            value={range}
            onChange={(e) => setRange(e)}
            options={['Today', 'This Week', 'This Month', 'Last Month', 'All Time']}
          />
        </div>
      </div>

      <div className="w-full flex gap-4">
        <ItemCard
          count={stats.plans.count}
          growing={stats.plans.growing}
          icon={PLANS_ICON}
          title="Total Plans"
          link="/plan-management"
          percentage={stats.plans.percentage}
          bgColor="bg-[#DDE0E9]"
        />
        <ItemCard
          count={stats.classes.count}
          growing={stats.classes.growing}
          icon={CLASSES_ICON}
          title="Total Classes"
          link="/class-management"
          percentage={stats.classes.percentage}
          bgColor="bg-[#D7F1ED]"
        />
        <ItemCard
          count={stats.students.count}
          growing={stats.students.growing}
          icon={STUDENTS_ICON}
          title="Total Students"
          link="/student-management"
          percentage={stats.students.percentage}
          bgColor="bg-[#B6CFFF]"
        />
        <ItemCard
          count={stats.tutors.count}
          growing={stats.tutors.growing}
          icon={TUTORS_ICON}
          title="Total Tutors"
          link="/tutor-management/manage"
          percentage={stats.tutors.percentage}
          bgColor="bg-[##ff660021]"
        />
      </div>

      <div className="w-full h-full flex gap-2">
        <div className="w-1/2 h-full p-7 flex flex-col gap-2 rounded-md bg-white drop-shadow-xl">
          <Earnings />
        </div>
        <div className="w-1/2  p-7 flex flex-col gap-2 rounded-md bg-white drop-shadow-xl">
          <Classes />
        </div>
      </div>
      <div className="w-full h-full flex gap-2">
        <div className="w-1/2 p-7 flex flex-col gap-2 rounded-md bg-white drop-shadow-xl">
          <ChangeRequests />
        </div>

        <div className="w-1/2 h-full p-7 flex flex-col gap-2 rounded-md bg-white drop-shadow-xl">
          <Map />
        </div>
      </div>
    </div>
  );
}

function Earnings() {
  const [chartData, setChartData] = useState<{ label: string; Earnings: number; Expenses: number }[]>([]);
  const [range, setRange] = useState('This Week');

  const getData = async (start: string, end: string) => {
    return await xhr({
      url: `/a/stats/earnings?startAfter=${start}&endBefore=${end}`,
      method: 'GET'
    })
      .then((res) => {
        if (res.status === 'OK') {
          return res.data;
        } else {
          return null;
        }
      })
      .catch((err) => err);
  };

  function daysInMonth(date: Date) {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  }

  useEffect(() => {
    if (range === 'This Week') {
      getData(startOfWeek(new Date()).toString(), endOfWeek(new Date()).toString()).then((res) => {
        let temp: any = [];
        let weekDay = startOfWeek(new Date());
        [...new Array(7)].map((_, i) => {
          let day = format(addDays(weekDay, i), 'EEE');
          let date = format(addDays(weekDay, i), 'd');
          const v = res.find((item: any) => format(parseISO(item.date), 'd') === date);
          temp.push({
            label: day,
            Earnings: (v && v.total) || 0,
            Expenses: 0
          });
        });
        setChartData(temp);
      });
    }
    if (range === 'This Month') {
      getData(startOfMonth(new Date()).toString(), endOfMonth(new Date()).toString()).then((res) => {
        let temp: any = [];
        let monthDay = startOfMonth(new Date());
        [...new Array(daysInMonth(new Date()))].map((_, i) => {
          let day = format(addDays(monthDay, i), 'LLL d');
          let date = format(addDays(monthDay, i), 'd');
          const v = res.find((item: any) => format(parseISO(item.date), 'd') === date);
          temp.push({
            label: day,
            Earnings: (v && v.total) || 0,
            Expenses: 0
          });
        });
        setChartData(temp);
      });
    }
    if (range === 'Last Month') {
      let month = subMonths(new Date(), 1);
      getData(startOfMonth(month).toString(), endOfMonth(month).toString()).then((res) => {
        let temp: any = [];
        let monthDay = startOfMonth(month);
        [...new Array(daysInMonth(month))].map((_, i) => {
          let day = format(addDays(monthDay, i), 'LLL d');
          let date = format(addDays(monthDay, i), 'd');
          const v = res.find((item: any) => format(parseISO(item.date), 'd') === date);
          temp.push({
            label: day,
            Earnings: (v && v.total) || 0,
            Expenses: 0
          });
        });
        setChartData(temp);
      });
    }
    if (range === 'This Year') {
      getData(startOfYear(new Date()).toString(), endOfYear(new Date()).toString()).then((res) => {
        let temp: any = [];
        let yearDay = startOfYear(new Date());
        [...new Array(12)].map((_, i) => {
          let day = format(addMonths(yearDay, i), 'LLL');
          const v = res.reduce((acc: any, currValue: any) => {
            if (format(parseJSON(currValue.date), 'LLL') == day) {
              return acc + currValue.total;
            } else {
              return acc;
            }
          }, 0);
          temp.push({
            label: day,
            Earnings: v || 0,
            Expenses: 0
          });
        });
        setChartData(temp);
      });
    }
  }, [range]);

  return (
    <>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Total Earnings</h1>
        <div className="w-full max-w-[164px]">
          <SelectDropDown
            placeholder="Select Range"
            value={range}
            onChange={(e) => setRange(e)}
            options={['This Week', 'This Month', 'Last Month', 'This Year']}
          />
        </div>
      </div>
      <h1 className="text-2xl font-bold text-green-500">
        INR {currencyFormatter(Number(chartData.reduce((acc, currValue) => acc + currValue.Earnings, 0)))}
      </h1>
      <div className="w-full h-[300px] text-sm my-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            width={500}
            height={300}
            data={chartData}
            margin={{
              top: 5,
              right: 30,
              left: 0,
              bottom: 5
            }}
          >
            <CartesianGrid vertical={false} strokeDasharray="1" />
            <XAxis dataKey="label" />
            <YAxis axisLine={false} tickLine={false} />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="Earnings"
              dot={false}
              stroke="#34C26D"
              activeDot={{ r: 8 }}
              strokeWidth={2}
            />
            <Line type="monotone" dataKey="Expenses" dot={false} stroke="#FF0000" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </>
  );
}

function Classes() {
  const [activeTab, setActiveTab] = useState('all');
  const onTabChange = (value: any) => {
    setActiveTab(value);
  };
  const [sessions, setSessions] = useState<
    { categoryType: ICategoryType; statuses: { status: ISessionStatus; count: number }[] }[]
  >([]);
  const [filteredSessions, setFilteredSessions] = useState<{
    pending: number;
    upcoming: number;
    completed: number;
    cancelled: number;
  }>({
    cancelled: 0,
    completed: 0,
    pending: 0,
    upcoming: 0
  });

  useEffect(() => {
    xhr({
      url: `/a/stats/sessions`,
      method: 'GET'
    })
      .then((res) => {
        if (res.status === 'OK') {
          setSessions(
            res.data.map((item: any) => ({
              categoryType: item._id,
              statuses: item.statuses
            }))
          );
        }
      })
      .catch((err) => err);
  }, []);

  useEffect(() => {
    if (activeTab === 'all') {
      let x = {
        completed: sessions.reduce(
          (acc, currvalue) => acc + currvalue.statuses.find((item) => item.status === 'completed')?.count! || 0,
          0
        ),
        upcoming: sessions.reduce(
          (acc, currvalue) => acc + currvalue.statuses.find((item) => item.status === 'upcoming')?.count! || 0,
          0
        ),
        pending: sessions.reduce(
          (acc, currvalue) => acc + currvalue.statuses.find((item) => item.status === 'pending')?.count! || 0,
          0
        ),
        cancelled: sessions.reduce(
          (acc, currvalue) => acc + currvalue.statuses.find((item) => item.status === 'cancelled')?.count! || 0,
          0
        )
      };
      setFilteredSessions(x);
      return;
    }
    let x = {
      upcoming:
        sessions.find((item) => item.categoryType === activeTab)?.statuses.find((item) => item.status === 'upcoming')
          ?.count || 0,
      pending:
        sessions.find((item) => item.categoryType === activeTab)?.statuses.find((item) => item.status === 'pending')
          ?.count || 0,
      completed:
        sessions.find((item) => item.categoryType === activeTab)?.statuses.find((item) => item.status === 'completed')
          ?.count || 0,
      cancelled:
        sessions.find((item) => item.categoryType === activeTab)?.statuses.find((item) => item.status === 'cancelled')
          ?.count || 0
    };
    setFilteredSessions(x);
  }, [activeTab, sessions]);

  return (
    <>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Total Classes</h1>
        <Link to={'/class-management'}>
          <Button text="View Details" size="sm" />
        </Link>
      </div>
      <div className="border-b">
        <TabBar value={activeTab} indicatorColor="bg-secondary" onChange={onTabChange} textColor="text-neutralGray1">
          <TabItem label="All" value="all" fontSize={15} />
          <TabItem label="Subjects  " value="subject" />
          <TabItem label="Hobby" value="hobby" />
          <TabItem label="Skill Development" value="skill" />
        </TabBar>
      </div>
      <div className="w-full h-full my-10 grid grid-cols-2 grid-rows-2 gap-4">
        <ClassesItem title="Pending" count={filteredSessions.pending} growing={true} percentage={8} />
        <ClassesItem title="Completed" count={filteredSessions.completed} growing={true} percentage={8} />
        <ClassesItem title="Upcoming" count={filteredSessions.upcoming} growing={false} percentage={90} />
        <ClassesItem title="Cancelled" count={filteredSessions.cancelled} growing={true} percentage={1} />
      </div>
    </>
  );
}

function ClassesItem({ title, count }: { title: string; count: number; growing: boolean; percentage: number }) {
  return (
    <div className="w-full p-4 flex flex-col border border-neutralGray3 rounded-md">
      <p className="text-sm font-semibold text-neutralGray2">{title}</p>
      <h1 className="my-4 text-2xl font-semibold">{count}</h1>
      {/* <div className="ml-auto flex items-center gap-2">
        {growing ?
          <>
            <p className="text-green-500">+{percentage}%</p>
            <img src={SUCCESS_ARROWUP_ICON} className="w-4" />
          </>
          :
          <>
            <p className="text-red-500">-{percentage}%</p>
            <img src={ERROR_ARROWDOWN_ICON} className="w-4" />
          </>
        }
      </div> */}
    </div>
  );
}

interface ItemCardProps {
  title: string;
  percentage: number;
  growing: boolean;
  count: number;
  link: string;
  icon: any;
  bgColor: string;
}

function ItemCard({ title, percentage, count, growing, icon, link, bgColor }: ItemCardProps) {
  return (
    <div className="w-full max-w-xs flex flex-col gap-2 bg-white p-4 border border-neutralGray3">
      <div className="flex items-center justify-between">
        <h6 className="text-sm font-medium">{title}</h6>
        <div className="flex gap-2">
          <h4 className="text-sm font-semibold text-green-400">
            {growing ? '+' : '-'}
            {percentage}%
          </h4>
          {growing ? <img src={RISING} className="w-4" /> : <img src={RISING} className="w-4" />}
        </div>
      </div>
      <div className="">
        <h1 className="text-xl font-semibold">{count}</h1>
      </div>

      <div className="flex items-end justify-between">
        <Link to={link} className="text-sm text-primary underline underline-offset-1 cursor-pointer">
          View Details
        </Link>
        <div className={`w-8 h-8 flex items-center justify-center p-2 ${bgColor} rounded-md`}>
          <img src={icon} />
        </div>
      </div>
    </div>
  );
}

function ChangeRequests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<LPlan[]>([]);
  useEffect(() => {
    plansApi
      .getChangeRequests(
        1,
        10,
        { status: "pending" },
        { createdAt: -1 },)
      .then((data) => {
        setRequests(data.results);
      });
  }, []);

  return (
    <>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Assign Tutor</h1>
      </div>
      {requests.length === 0 ? (
        <h1 className="text-base font-semibold text-center my-10">No Change Requests</h1>
      ) : (
        <div className="w-full h-full overflow-x-scroll custom-scrollbar">
          <Table>
            <TableHeading>
              <TableRow>
                <TableHeadingCol label="DATE" />
                <TableHeadingCol label="PLAN ID" />
                <TableHeadingCol label="STUDENT" />
                <TableHeadingCol label="STATUS" />
              </TableRow>
            </TableHeading>
            <TableBody>
              {requests.map((request, key) => (
                <TableRow key={key} onClick={() => navigate(`/plan-management/change-requests/${request._id}`)}>
                  <TableRowItemData text={format(parseISO(request.createdAt), 'dd/MM/yyyy')} />
                  <TableRowItemData text={request._id} />
                  <TableRowItemData text={request.user.name} />
                  <TableRowItemData
                    component={
                      <StatusChip
                        expand={false}
                        state={
                          request.changeRequest?.status === 'approved'
                            ? 'success'
                            : request.changeRequest?.status === 'rejected'
                              ? 'error'
                              : 'pending'
                        }
                      >
                        <p className="font-semibold text-[11px] w-24">
                          {request.changeRequest?.status === 'pending'
                            ? 'NEW'
                            : request.changeRequest?.status === 'approved'
                              ? 'ACCEPTED'
                              : 'REJECTED'}
                        </p>
                      </StatusChip>
                    }
                  />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );
}

function Map() {
  const [range, setRange] = useState('This Week');
  const [studentLocations, setStudentLocations] = useState<{ country: string; count: number }[]>([]);
  const [tutorsLocations, setTutorsLocations] = useState<{ country: string; count: number }[]>([]);
  const [mapData, setMapData] = useState<DataItem<number>[]>([]);
  const [currRangeDate, setCurrRangeDate] = useState({
    start: '',
    end: ''
  });
  useEffect(() => {
    let m: { [x: string]: number } = {};
    studentLocations.forEach((location) =>
      m[location.country] ? (m[location.country] += location.count) : (m[location.country] = location.count)
    );
    tutorsLocations.forEach((location) =>
      m[location.country] ? (m[location.country] += location.count) : (m[location.country] = location.count)
    );
    setMapData(Object.keys(m).map((key) => ({ country: key as ISOCode, value: m[key] })));
  }, [studentLocations, tutorsLocations]);

  useEffect(() => {
    if (!currRangeDate.start) {
      return;
    }

    xhr({
      url: `/a/stats/location?startAfter=${currRangeDate.start}&endBefore=${currRangeDate.end}`,
      method: 'GET'
    })
      .then((res) => {
        if (res.status === 'OK') {
          if (res.data.length === 0) {
            setStudentLocations([]);
            setTutorsLocations([]);
            return;
          }
          res.data.map((item: any) => {
            if (item._id === 'student') {
              setStudentLocations(item.countries);
            }
            if (item._id === 'tutor') {
              setTutorsLocations(item.countries);
            }
          });
        }
      })
      .catch((err) => err);
  }, [currRangeDate]);

  useEffect(() => {
    if (range === 'This Week') {
      setCurrRangeDate({
        start: startOfWeek(new Date()).toString(),
        end: endOfWeek(new Date()).toString()
      });
    }
    if (range === 'This Month') {
      setCurrRangeDate({
        start: startOfMonth(new Date()).toString(),
        end: endOfMonth(new Date()).toString()
      });
    }
    if (range === 'Last Month') {
      setCurrRangeDate({
        start: startOfMonth(subMonths(new Date(), 1)).toString(),
        end: endOfMonth(subMonths(new Date(), 1)).toString()
      });
    }
    if (range === 'All Time') {
      setCurrRangeDate({
        start: new Date(0).toString(),
        end: ''
      });
    }
  }, [range]);

  return (
    <>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Users By Location</h1>
        <div className="w-full max-w-[164px]">
          <SelectDropDown
            placeholder="Select Range"
            value={range}
            onChange={(e) => setRange(e)}
            options={['This Week', 'This Month', 'Last Month', 'This Year']}
          />
        </div>
      </div>
      <WorldMap color="#2B5297" title="" value-suffix="people" size="lg" data={mapData} />
      <div className="w-full flex gap-5">
        <div className="w-full flex flex-col gap-2">
          <h1 className="text-xl font-semibold text-primary mb-4">Tutors</h1>
          {tutorsLocations.map((location, key) => (
            <div key={key} className="flex justify-between">
              <p className="text-sm font-medium">{CountryQuery.find('cca2', location.country).name.common}</p>
              <p className="text-sm font-medium">
                {((location.count / tutorsLocations.reduce((acc, value) => acc + value.count, 0)) * 100).toFixed(2)}%
              </p>
            </div>
          ))}
        </div>
        <div className="w-full flex flex-col gap-2">
          <h1 className="text-xl font-semibold text-primary mb-4">Students</h1>
          {studentLocations.map((location, key) => (
            <div key={key} className="flex justify-between">
              <p className="text-sm font-medium">{CountryQuery.find('cca2', location.country).name.common}</p>
              <p className="text-sm font-medium">
                {((location.count / studentLocations.reduce((acc, value) => acc + value.count, 0)) * 100).toFixed(2)}%
              </p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
